/**
 * Which social platforms the package lets this brand NEWLY connect.
 *
 * Source: GET /api/teams/quota-usage (`platforms` + `mode`), falling back to the entitlements payload of
 * GET /api/teams/package. Unrestricted packages allow every platform. Nothing is locked unless the backend mode
 * is 'enforce'. Channels that already exist on a disallowed platform are grandfathered by the backend (they keep
 * publishing and can reconnect); only new connections are refused.
 */
import { useContext } from 'react';
import { PackageContext } from '@/contexts/PackageContext';
import { useQuotaUsage } from '@/hooks/useQuota';
import { GATED_PLATFORMS, type GatingMode, type PlatformRestriction } from '@/types/package';

export interface PlatformAccess {
  isLoading: boolean;
  restricted: boolean;
  mode: GatingMode;
  /** The package includes the platform (always true when unrestricted) */
  isPlatformAllowed: (platform: string) => boolean;
  /** The UI should show the platform as locked (restricted + not allowed + enforce) */
  isPlatformLocked: (platform: string) => boolean;
  /** Cheapest plan slug that includes the platform, exactly as the backend reports it (null = unknown) */
  upsellPlanSlugFor: (platform: string) => string | null;
}

/** Facebook and Instagram connect through the same Meta OAuth start: either one allowed lets the flow start. */
const META_PLATFORMS: ReadonlySet<string> = new Set(['FACEBOOK', 'INSTAGRAM']);

export const usePlatformAccess = (): PlatformAccess => {
  const context = useContext(PackageContext);
  const { data, isLoading } = useQuotaUsage();

  const platforms: PlatformRestriction | undefined = data?.platforms ?? context?.entitlements?.platforms;
  const mode: GatingMode = data?.mode ?? context?.entitlements?.mode ?? 'off';
  const restricted = platforms?.restricted === true;
  const allowedList = platforms?.allowed ?? [];

  const isPlatformAllowed = (platform: string): boolean => {
    // Only the 9 social platforms are gated here; Canva / Google Drive / WordPress use their feature gates
    if (!restricted || !(GATED_PLATFORMS as readonly string[]).includes(platform)) return true;
    if (META_PLATFORMS.has(platform)) return allowedList.some((p) => META_PLATFORMS.has(p));
    return allowedList.includes(platform);
  };

  return {
    isLoading,
    restricted,
    mode,
    isPlatformAllowed,
    isPlatformLocked: (platform) => mode === 'enforce' && !isPlatformAllowed(platform),
    upsellPlanSlugFor: (platform) => platforms?.upsellPlanSlugs?.[platform] ?? null,
  };
};

export default usePlatformAccess;
